import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Clock, LogIn, LogOut } from "lucide-react";

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

export default function Attendance() {
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  const [selected, setSelected] = useState("");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));

  const load = async () => {
    try {
      const { data } = await api.get("/attendance", { params: { month } });
      setRecords(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/employees");
        setEmployees(data);
        if (data.length > 0) setSelected(data[0].id);
      } catch (e) {
        // noop
      }
    })();
  }, []);

  useEffect(() => { load(); }, [month]);

  const checkIn = async () => {
    if (!selected) return toast.error("Select an employee");
    try {
      await api.post("/attendance/check-in", { employee_id: selected });
      toast.success("Checked in");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const checkOut = async () => {
    if (!selected) return toast.error("Select an employee");
    try {
      await api.post("/attendance/check-out", { employee_id: selected });
      toast.success("Checked out");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const empMap = Object.fromEntries(employees.map((e) => [e.id, e]));

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div>
        <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 mb-2">
          Time &amp; Attendance
        </div>
        <h1 className="font-heading text-4xl font-black tracking-tight">Attendance</h1>
        <p className="text-sm text-zinc-500 mt-1">Check-in / check-out logs across the workforce.</p>
      </div>

      <div className="bg-white border border-zinc-200 p-6 rounded-sm">
        <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 mb-4">
          Punch Clock
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger className="rounded-sm h-11 border-zinc-300" data-testid="att-select-emp">
                <SelectValue placeholder="Select employee" />
              </SelectTrigger>
              <SelectContent>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={checkIn}
            data-testid="att-checkin-btn"
            className="btn-pressable rounded-sm h-11 bg-[#09090B] hover:bg-zinc-800 text-white font-mono uppercase tracking-widest text-xs"
          >
            <LogIn className="h-4 w-4 mr-2" /> Check In
          </Button>
          <Button
            onClick={checkOut}
            variant="outline"
            data-testid="att-checkout-btn"
            className="btn-pressable rounded-sm h-11 border-zinc-300 font-mono uppercase tracking-widest text-xs"
          >
            <LogOut className="h-4 w-4 mr-2" /> Check Out
          </Button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500">Log</div>
            <h3 className="font-heading text-lg font-bold tracking-tight">Records</h3>
          </div>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="font-mono text-sm border border-zinc-300 px-3 py-1.5 rounded-sm"
            data-testid="att-month"
          />
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50 hover:bg-zinc-50">
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Date</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Employee</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Check-in</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Check-out</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-16 text-sm text-zinc-500">
                  <Clock className="h-8 w-8 mx-auto mb-3 text-zinc-300" />
                  No attendance records for this month.
                </TableCell>
              </TableRow>
            )}
            {records.map((r) => (
              <TableRow key={r.id} className="hover:bg-zinc-50" data-testid={`att-row-${r.id}`}>
                <TableCell className="font-mono text-sm">{r.date}</TableCell>
                <TableCell className="text-sm">{empMap[r.employee_id]?.full_name || r.employee_id}</TableCell>
                <TableCell className="font-mono text-sm">{fmtTime(r.check_in)}</TableCell>
                <TableCell className="font-mono text-sm">{fmtTime(r.check_out)}</TableCell>
                <TableCell>
                  <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm bg-emerald-100 text-emerald-700">
                    {r.status}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
